import { useEffect, useState } from "react";
import { getContactDetailsRoute, postContactAddressRoute } from "@api-routes/contacts";
import { useFetch } from "@common/hook/useFetch";
import { useUserContext } from "@hook/useUserContext";
import { areTwoSameAddresses, filterAddresses } from "@features/Addons/utils";
import { EADDRESS_TYPES, OFFER_CONFIG } from "@constants";
import { postAddonRoute } from "@api-routes/addons";
import {
    getResponseToContactState,
    IUserContactResponse,
} from "@features/Profile/UserDetails/mappers/getUserDetailsMapper";
import { IContactAddress } from "@common/Eircode/interface/IAddressForm";
import { IUserContactDetails } from "@features/Profile/interface/IUserDetails";

interface IParams {
    onClose(): void;
    getAddonDetails(): void;
}

export const useAddAddonModal = (props: IParams) => {
    const { onClose, getAddonDetails } = props;
    const { username: contactUuid } = useUserContext();

    const [getContactState, { get: getContact }] = useFetch();
    const [postContactAddressState, { post: postContactAddress }] = useFetch();
    const [postAddonState, { post: postAddon }] = useFetch();

    const [contactDetails, setContactDetails] = useState<IUserContactDetails>();
    const [selectedContactAddress, setSelectedContactAddress] = useState<IContactAddress>();

    useEffect(() => {
        if (contactUuid) {
            getContact<IUserContactResponse>(getContactDetailsRoute(contactUuid)).then((response) => {
                if (response) {
                    const mappedUserContact = getResponseToContactState(response);

                    const filteredAddressList: IContactAddress[] = filterAddresses(mappedUserContact.addresses);
                    setContactDetails({
                        ...mappedUserContact,
                        addresses: areTwoSameAddresses(filteredAddressList)
                            ? filteredAddressList.slice(0, 1)
                            : filteredAddressList,
                    });
                    setSelectedContactAddress(filteredAddressList[0]);
                }
            });
        }
    }, [contactUuid, getContact]);

    const createAddon = async (serviceId: number, addonCode: string, deliveryContactUuid: string) => {
        await postAddon(postAddonRoute(), {
            body: {
                addonCode,
                channelCode: OFFER_CONFIG.CHANNEL,
                deliveryContactUuid,
                serviceId,
            },
        }).then((response) => {
            if (response) {
                getAddonDetails();
                onClose();
            }
        });
    };

    const createDeliveryContactAddress = () => {
        if (!contactDetails || !selectedContactAddress) {
            return null;
        }

        return postContactAddress<string>(postContactAddressRoute(), {
            body: {
                emails: contactDetails.emails,
                firstName: contactDetails.firstName,
                lastName: contactDetails.lastName,
                phoneNumbers: contactDetails.phoneNumbers,
                addresses: [
                    {
                        addressLine1: selectedContactAddress.addressLine1,
                        addressLine2: selectedContactAddress.addressLine2,
                        addressLine3: selectedContactAddress.addressLine3,
                        code: selectedContactAddress.code,
                        county: selectedContactAddress.county,
                        town: selectedContactAddress.town,
                        type: EADDRESS_TYPES.DELIVERY,
                    },
                ],
            },
        });
    };

    const handleConfirmAddAddon = async (serviceId: number, addonCode: string) => {
        const deliveryContactUuid = await createDeliveryContactAddress();
        if (!deliveryContactUuid) {
            return;
        }

        await createAddon(serviceId, addonCode, deliveryContactUuid);
    };

    const isLoading = getContactState.isLoading || postContactAddressState.isLoading || postAddonState.isLoading;
    const error = getContactState.error ?? postContactAddressState.error ?? postAddonState.error;

    return {
        contactAddresses: contactDetails?.addresses ?? [],
        selectedContactAddress,
        setSelectedContactAddress,
        handleConfirmAddAddon,
        error,
        isLoading,
    };
};
